import Link from "next/link";
import cls from "classnames";
import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/nextjs";

import UserAvatar from "@qoohoo/common/components/UserAvatar";
import ShareButtonGroup from "./ShareButtonGroup";
import Description from "./Description";
import ChannelNotAvailable from "./ChannelNotAvailable";
import JoinTelegramButton from "./JoinTelegramButton";
import TagGroup from "./TagGroup";
import JoinModals from "../JoinModals";
import FAQSection from "./FAQSection";
import CreatorNavBar from "components/Navbar/CreatorNavbar";
import CreatorFooter from "../../CreatorFooter";

import useSessionStore from "../../../store/sessionStore";
import httpf from "../../../utils/frontend/httpf";
import * as Track from "@qoohoo/common/utils/analytics/track";
import useHasHydrated from "@qoohoo/common/hooks/useHasHydrated";
import { button, link, pageview } from "@qoohoo/common/constants/analytics";
import PaymentBanner from "../../premium-content/PaymentBanner";
import useStore from "../../../store/store";
import Coupon from "../../coupon/Coupon";
import {calcSeatStatus, getTemporalLabel} from "@qoohoo/common/helpers/channel";
import { LimitedSeatsRibbon } from "../../channels/Join";
import MembershipDurationLabel from "../../MembershipDurationLabel";
import {MultiCurrency} from "@qoohoo/common/utils/currency";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import {enableNewCheckoutForFeature} from "constants/constants";

export function fetchUserChannelDetail(id) {
  return httpf
    .post(`/api/auth-channel-details`, {
      id,
    })
    .catch((err) => {
      console.error(err);
      Sentry.captureException(err);

      throw err;
    });
}

const currentScreen = pageview.channel_details;

const TelegramPost = ({ channelDetails, isWhiteTheme, customConfig }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [userChannelDetail, setUserChannelDetail] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const setReadyToPay = useStore((state) => state.setReadyToPay);

  const isSubscribed = userChannelDetail.is_channel_member;
  const is_private = channelDetails.is_private;
  const showTaxDetails = !channelDetails.is_tax_inclusive;

  const userData = useSessionStore((state) => state.userData);
  const country = useStore((state) => state.country);
  const loadingStore = useStore((state) => state.loading);

  const isHydrated = useHasHydrated();

  const creatorUsernameOrFullname =
    channelDetails.creator?.fullname || channelDetails.creator.username;
  const isVerifiedCreator = channelDetails.creator.is_verified_creator;
  const telegramChannelType = channelDetails.telegram.telegram_chat_type;
  const channelImageUrl = channelDetails.center_image?.url;

  const ctaButtonText =
    userChannelDetail.cta_button?.button_text ||
    channelDetails.cta_button.button_text;
  const ctaButtonAction =
    userChannelDetail.cta_button?.action || channelDetails.cta_button.action;
  const isChannelArchived = channelDetails.is_archived;
  const isChannelEnded = channelDetails.is_ended;

  const showMembershipDurationLabel = isSubscribed && !isChannelArchived;

  const isCreator = channelDetails?.creator?.uid === userData?.uid;
  const isCoCreator = useMemo(() => {
    const co_creator_uids = userChannelDetail?.co_creators?.map(
      (creator) => creator?.uid
    );

    const userId = userData?.uid;

    return co_creator_uids?.includes(userId);
  }, [userData?.uid, userChannelDetail.co_creators]);

  const seatStatus =
    !channelDetails.has_limited_seats ||
    ctaButtonAction === null ||
    isSubscribed ||
    isCreator ||
    isCoCreator ||
    isChannelArchived ||
    isChannelEnded
      ? ""
      : calcSeatStatus(
          channelDetails.max_subscribers,
          channelDetails.total_seats_left
        );

  const areFewSeatsLeft = seatStatus.includes("left");
  const areSeatsFull = seatStatus.includes("No seats");

  const inviteLinkReadyStates = [
    "TELEGRAM_JOIN_PENDING",
    "TELEGRAM_JOIN_SUCCESS",
  ];

  const isLinkReady = inviteLinkReadyStates.includes(
    userChannelDetail.telegram?.state || channelDetails.telegram.state
  );

  const hasMultiplePlans = channelDetails.plans.length > 1;

  const isLifetime = userChannelDetail.duration?.toLowerCase() === "lifetime";
  const isOneTime = channelDetails.type.toLowerCase() === "one_time";

  const trackDetails = {
    ...channelDetails,
    current_screen: currentScreen,
    is_telegram: true,
    uid: channelDetails.uid,
    "creator.uid": channelDetails.creator.uid,
    "creator.full_name": channelDetails.creator.fullname,
    "creator.username": channelDetails.creator.username,
    is_verified_creator: channelDetails.creator.is_verified_creator,
    telegram_invite_link: userChannelDetail.telegram?.invite_link,
    telegram_state: userChannelDetail.telegram?.state,
    telegram_chat_type: telegramChannelType,
    channel_status: ctaButtonText,
    currentUser: userData?.uid,
  };

  function handleButtonClick() {
    if (!userData?.uid) {
      setModalOpen(true);
    } else {
      if (hasMultiplePlans) {
        setModalOpen(true);
      } else {
        setModalOpen(true);
        setReadyToPay(true);
      }

      Track.buttonClick(button.cta_btn, trackDetails);
    }
  }

  function getUserChannelDetail() {
    setIsLoading(true);

    fetchUserChannelDetail(channelDetails.uid)
      .then((res) => {
        setUserChannelDetail(res.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const prepareCouponResponse = (coupon) => {
    return {
      coupon,
      channel: channelDetails.uid,
    };
  };

  const onCouponValidation = (data) => {
    setCouponState(data);
  };

  const [couponState, setCouponState] = useState({});

  const showCouponField =
    !isChannelEnded &&
    !isChannelArchived &&
    channelDetails?.is_paid &&
    !isCreator &&
    !isCoCreator &&
    (isSubscribed ? !(isLifetime || isOneTime) : !areSeatsFull);

  useEffect(() => {
    if (userData?.uid) {
      getUserChannelDetail();
    }
  }, [userData]);

  if (isChannelArchived || isChannelEnded) {
    const creatorProfileLink = `/by/${channelDetails.creator.username}`;
    return (
      <div className="w-full sm:mb-0 min-h-screen flex flex-col justify-between items-stretch">
        <div>
          <CreatorNavBar creator={channelDetails.creator} currentScreen={currentScreen} isDark={!isWhiteTheme}/>
          <ChannelNotAvailable creatorProfileLink={creatorProfileLink} />
        </div>
        <CreatorFooter />
      </div>
    );
  }

  function renderJoinButton(isFooter = false) {
    const enableNewCheckout = enableNewCheckoutForFeature.telegram_channel;

    let joinButton = null;

    if (isSubscribed && (isLifetime || isOneTime)) {
      joinButton = null;
    } else if (isCreator || isCoCreator) {
      if (isFooter) {
        joinButton = null;
      } else {
        joinButton = (
          <div className="mt-6 pt-6 text-left border-t border-[#2B353B]">
            <p className="text-xs leading-[150%]">
              You're the creator of this Telegram group.
            </p>
          </div>
        );
      }
    } else {
      joinButton = (
        <div
          className={cls(
            "flex-col items-center text-center z-[1]",
            isFooter
              ? "sm:hidden flex sticky bottom-0 w-full px-7 pb-10 pt-4 bg-[#262C30] dark:bg-white dark:border border-[#D5D8DC]"
              : "hidden sm:flex sm:relative sm:mx-auto sm:py-0 sm:px-0 sm:bg-transparent"
          )}
        >

          {
            !enableNewCheckout && (
              <>
                {showCouponField && (
                  <Coupon
                    state={couponState}
                    prepareResponse={prepareCouponResponse}
                    onValidation={onCouponValidation}
                    className="mb-4"
                  />
                )}
                <JoinTelegramButton
                  onClick={handleButtonClick}
                  channelDetails={channelDetails}
                  userChannelDetail={userChannelDetail}
                  isLoading={!isHydrated || isLoading || loadingStore}
                  couponState={couponState}
                  showTaxDetails={showTaxDetails}
                  custom_button_label={customConfig?.custom_button_label}
                  isDisabled={
                    areSeatsFull && !isSubscribed && !isCreator && !isCoCreator
                  }
                />
              </>

            )
          }

          {
            enableNewCheckout && (
              <SuperCheckoutButton
                channelDetails={channelDetails}
                userChannelDetail={userChannelDetail}
                userData={userData}
                customConfig={customConfig}
                isLoading={!isHydrated || isLoading || loadingStore}
                isDisabled={
                  areSeatsFull && !isSubscribed && !isCreator && !isCoCreator
                }
              />
            )
          }
        </div>
      );
    }

    return joinButton;
  }

  return (
    <>
      <div
        className={cls(
          "w-full sm:mb-0 min-h-screen flex flex-col justify-between items-stretch",
          { dark: isWhiteTheme }
        )}
      >
        <div className="dark:bg-[#F5F5F5]">
          <CreatorNavBar creator={channelDetails.creator} currentScreen={currentScreen} isDark={!isWhiteTheme}/>

          <main className="sm:mt-5 mb-20 sm:mb-44 mx-auto text-white dark:bg-[#F5F5F5]">
            <MembershipDurationLabel
              isHeaderMode={true}
              isVisible={showMembershipDurationLabel}
              link={userChannelDetail?.telegram?.invite_link}
              linkLabel="Join on Telegram"
              userChannelDetail={userChannelDetail}
              colFormat={true}
            />
            <div className="relative w-auto mx-auto sm:w-auto">
              <div className="relative mx-auto w-full sm:w-[484px] sm:border sm:border-[#2b353b] sm:bg-[#161a1d] sm:rounded-2xl dark:sm:bg-[#FFFFFF] dark:border-[#D5D8DC]">
                <div className="px-6 pt-11 sm:px-9 sm:pt-9 sm:pb-2">
                  <LimitedSeatsRibbon seatStatus={seatStatus} />
                  <MembershipDurationLabel
                    isHeaderMode={false}
                    isVisible={showMembershipDurationLabel}
                    link={userChannelDetail?.telegram?.invite_link}
                    linkLabel="Join on Telegram"
                    userChannelDetail={userChannelDetail}
                    colFormat={true}
                  />
                  <div
                    className={cls(
                      "flex flex-col sm:flex-row space-y-5 sm:space-y-0 sm:space-x-8 items-center",
                      {
                        "mt-5": !!seatStatus,
                      }
                    )}
                  >
                    {!!channelImageUrl && (
                      <div className="relative max-h-[148px] sm:max-h-28 max-w-[148px] sm:min-w-[112px] w-[148px] sm:w-28">
                        <img
                          src={channelDetails.center_image.url}
                          className="rounded-[28px] overflow-hidden w-full h-full object-cover"
                          alt={channelDetails.name}
                        />
                      </div>
                    )}
                    <div
                      className={cls(
                        "self-start sm:self-auto space-y-6 sm:space-y-4 max-w-full",
                        !!channelImageUrl && "max-w-full sm:max-w-[268px]"
                      )}
                    >
                      <h1
                        className={cls(
                          "font-bold text-2xl leading-[150%] whitespace-pre-wrap line-clamp-3 dark:text-[#16191B]",
                          !!channelImageUrl && "sm:max-w-[260px]"
                        )}
                      >
                        {channelDetails.name}
                      </h1>
                      <Link
                        href={`/by/${channelDetails.creator.username}`}
                        passHref
                        target="_blank"
                        className="flex cursor-pointer max-w-full"
                      >
                        <div className="space-x-2 flex items-center">
                          <div className="flex justify-center items-center text-sm font-semibold object-cover min-w-[28px] sm:min-w-[24px] w-7 sm:w-6 h-7 sm:h-6 overflow-hidden rounded-full bg-[#2b3135]">
                            <UserAvatar
                              avatarUrl={channelDetails.creator.avatar?.url}
                              userName={creatorUsernameOrFullname}
                            />
                          </div>
                          <div
                            className={cls(
                              "flex items-center text-sm leading-4 capitalize max-w-[85%] dark:text-[#16191B]"
                            )}
                          >
                            <span className={cls("truncate sm:max-w-xs")}>
                              {creatorUsernameOrFullname}
                            </span>
                            {isVerifiedCreator && (
                              <img
                                src="/assets/icons/round-verified.svg"
                                alt="verified icon"
                                className="w-5 h-5 ml-2"
                              />
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>

                  <TagGroup channelDetails={channelDetails} />

                  <Description
                    text={channelDetails.description}
                    className="text-sm leading-5 mt-6"
                    trackDetails={trackDetails}
                  />
                </div>
                <div
                  className={cls(
                    "border-t py-5 px-5 mt-4",
                    "rounded-b-2xl hidden sm:block",
                    isWhiteTheme ? "bg-white border-t-[#d6d8dc]" : "bg-[#212327] border-t-[#333A3F]"
                  )}
                >
                  {renderJoinButton()}
                </div>

                {/* Share Button Group */}
                {isSubscribed && (
                  <ShareButtonGroup
                    channelName={channelDetails.name}
                    channelType={telegramChannelType}
                    shareUrl={channelDetails.short_url_flutter}
                    creatorDetails={channelDetails.creator}
                    trackDetails={trackDetails}
                    isWhiteTheme={isWhiteTheme}
                  />
                )}
              </div>

              {/* Payment Banner */}
              {!isLinkReady && (
                <div className="pt-5 pb-1 md:p-0">
                  <PaymentBanner country={country} />
                </div>
              )}
            </div>

            <FAQSection
              channelDetails={channelDetails}
              customConfig={customConfig}
              isWhiteTheme={isWhiteTheme}
            />
          </main>

          {modalOpen ? (
            <JoinModals
              modalOpen={modalOpen}
              setModalOpen={setModalOpen}
              channelDetails={channelDetails}
              userChannelDetail={userChannelDetail}
              isSubscribed={isSubscribed}
              isPrivate={is_private}
              isCreator={isCreator}
              isCoCreator={isCoCreator}
              getUserChannelDetail={getUserChannelDetail}
              couponState={couponState}
              prepareCouponResponse={prepareCouponResponse}
              onCouponValidation={onCouponValidation}
              setIsLoading={setIsLoading}
              isLoadingApiResponse={isLoading}
              isWhiteTheme={isWhiteTheme}
              customConfig={customConfig}
              showTaxDetails={showTaxDetails}
            />
          ) : null}
        </div>
        <CreatorFooter isWhiteTheme={isWhiteTheme} />
        {renderJoinButton(true)}
      </div>
    </>
  );
};

export default TelegramPost;


export function SuperCheckoutButton({channelDetails, userChannelDetail, userData, className, isLoading, isDisabled, customConfig }) {
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [isCheckoutOpened, setIsCheckoutOpened] = useState(false);

  const checkoutSettings = customConfig?.settings?.checkout;

  useEffect(() => {
    if(isPaymentSuccess && !isCheckoutOpened) {
      location.reload();
    }
  }, [isPaymentSuccess, isCheckoutOpened]);

  function getButtonLabels() {
    const isSubscribed = userChannelDetail?.is_channel_member;
    const isChannelArchived = channelDetails.is_archived;
    const isChannelEnded = channelDetails.is_ended;

    let label, subLabel;

    if (isLoading) {
      label = <Spinner size="xs" />;
    } else if (isChannelArchived) {
      label = "Channel archived";
    } else if (isChannelEnded) {
      label = "Channel ended";
    } else if (!isSubscribed) {
      const channelFeeLabels = getChannelFeeLabel();
      label = channelFeeLabels.label;
      subLabel = channelFeeLabels.subLabel;
    } else {
      label = "Extend Membership";
    }

    return {
      label,
      subLabel
    };
  }

  // Note: Using SuperCheckoutButton only for recurring and one time payment - not free / courses / etc
  function getChannelFeeLabel() {
    let label, subLabel;

    const isOneTimePayment = channelDetails.type?.toLowerCase() === "one_time";
    const isRecurring = channelDetails.type === "recurring";
    const hasMultiplePlans = channelDetails?.plans?.length > 1;

    const firstChannelPlan = channelDetails?.plans?.[0];

    const currency = channelDetails?.currency;
    let amountToPay;

    if (isOneTimePayment) {
      amountToPay = channelDetails?.channel_fee;
    } else {
      amountToPay = firstChannelPlan?.amount_after_discount || firstChannelPlan?.amount;
    }

    const amountToPayLabel = MultiCurrency.formatAmountWithSymbol(
      amountToPay,
      currency
    );

    if (isRecurring) {
      if (hasMultiplePlans) {
        label = "Subscribe Now"
        subLabel = "Multiple plans available"
        
        if (customConfig?.custom_button_label?.recurring) {
          label = customConfig?.custom_button_label?.recurring;
          subLabel = '';
        }
      } else {
        label = `Subscribe for ${amountToPayLabel}`

        subLabel = getTemporalLabel(
          {
            channelType: channelDetails.type,
            start_date: null,
            planDetails: {
              planType: firstChannelPlan?.plan_type,
              interval: firstChannelPlan?.interval,
              duration: firstChannelPlan?.duration,
            },
          },
          false
        ).toLowerCase();

        if (customConfig?.custom_button_label?.recurring_single_plan) {
          label = customConfig?.custom_button_label?.recurring_single_plan;
          subLabel = '';
        }
      }
    } else if (isOneTimePayment) {
      label = `Pay ${amountToPayLabel}`
    }

    return {
      label,
      subLabel
    }
  }

  const handleOnPaymentSuccess = (...args) => {
    console.log('web - payment success...', args);
    setIsPaymentSuccess(true);
  };

  const handleOnClose = (...args) => {
    console.log('web - closed payment modal...', args);
    setIsCheckoutOpened(false);
  };

  function openCheckout() {
    const options = {
      entity_type: "channel",
      entity_id: channelDetails.uid,
      on_payment_success: handleOnPaymentSuccess,
      on_close: handleOnClose,
      notes: {}
    };

    if(window.qoohoo) {
      const checkout = new qoohoo(options);
      checkout.open();
      setIsCheckoutOpened(true);
    }
  }

  const buttonLabels = getButtonLabels();

  return (
    <button
      className={cls(
        "text-center mx-auto relative w-full py-3 hover:opacity-90 bg-gradient-to-t from-[#229ED9] to-[#2AABEE]",
        "outline-none focus:outline-none rounded-md",
        className,
        isDisabled && "opacity-60 hover:!opacity-60 cursor-not-allowed",
      )}
      disabled={isDisabled}
      onClick={openCheckout}
    >
      <div className="font-bold leading-[18px] relative">
        {buttonLabels.label}
      </div>
      {
        !!buttonLabels.subLabel && (
          <div className="text-[11px] leading-[14px] font-normal capitalize">
            {buttonLabels.subLabel}
          </div>
        )
      }
    </button>
  );
}